// DocNavDrawerProvider / useDocNavDrawer (RFC-0014 Phase 3 Task 3.1).
//
// Open/close state for the doc-reader's page-scoped "Contents" navigation drawer.
// A SEPARATE provider from the app-shell drawer (binding decision #3 — do NOT
// share state, and do NOT build a unified overlay-stack manager): a reader page
// legitimately has BOTH the global hamburger (app-shell drawer, below `md`) AND
// its own "Contents" trigger (this drawer, below `lg`), so their open state must
// be independent.
//
// Mirrors context/app-shell-drawer.tsx exactly: { isOpen, open, close }, a safe
// no-op default outside a provider, and route-change auto-close via useLocation
// (following a doc link inside the drawer navigates → the drawer closes itself).
//
// Note: exporting both the provider and the hook from one file triggers the same
// accepted `react-refresh/only-export-components` lint *warning* as
// app-shell-drawer.tsx / connect-repo.tsx — lint still passes; mirrors the repo
// pattern.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';

interface DocNavDrawerContextValue {
  /** Whether the doc-reader "Contents" drawer is currently open. */
  isOpen: boolean;
  /** Open the drawer. */
  open: () => void;
  /** Close the drawer. */
  close: () => void;
}

const DocNavDrawerContext = createContext<DocNavDrawerContextValue | null>(null);

export function DocNavDrawerProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  // Route-change auto-close: selecting a doc in the drawer navigates to it, which
  // should collapse the drawer over the newly-rendered page. Keyed on pathname
  // only (NOT isOpen) to avoid an update→re-run loop — closing an already-closed
  // drawer is a no-op.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  const value = useMemo<DocNavDrawerContextValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <DocNavDrawerContext.Provider value={value}>{children}</DocNavDrawerContext.Provider>
  );
}

/**
 * Access the doc-reader drawer controls. Returns safe no-op defaults when called
 * outside a provider (never throws), matching app-shell-drawer.tsx's posture.
 */
export function useDocNavDrawer(): DocNavDrawerContextValue {
  const ctx = useContext(DocNavDrawerContext);
  if (ctx) return ctx;
  return { isOpen: false, open: () => {}, close: () => {} };
}
